import camelCase from 'lodash/camelCase';
import { createFlagClient, createNoopFlagClient } from '@apptegy/ld-feature-flags';
import { provideFlags } from '@apptegy/ld-feature-flags/nuxt';
import { createMockFlagClient } from '@apptegy/ld-feature-flags/testing';
import { defineNuxtPlugin } from '#app';

/**
 * Local-dev flag overrides, mirroring the Rails convention used by auth-service,
 * thrillshare and rooms-backend (`config/flags/feature_flags.json` holding `flagValues`,
 * read there via LaunchDarkly::Integrations::FileData).
 *
 * LAUNCH_DARKLY_KEY is a build-time ARG supplied by Jenkins and is not provisioned
 * locally, so the no-client branch below is always the one taken during `nuxi dev`.
 *
 * Unlike the Rails services, the file is committed rather than gitignored: it gives
 * every developer a working local default with no setup, and changes to it show up in
 * review. Take care not to commit incidental debugging tweaks.
 *
 * `import.meta.dev` is what keeps these values out of deployed environments — Vite
 * statically replaces it with `false` in `pnpm generate`, so this whole branch is
 * dead-code-eliminated and the JSON never reaches the bundle. That matters because
 * production CAN reach the no-client branch below (an admin with no `client`), so the
 * guard, not the absence of the file, is the thing doing the work.
 *
 * `import.meta.glob` rather than a static import so a missing file yields {} instead of
 * breaking the build. `createMockFlagClient` does no key transformation of its own
 * (unlike `createFlagClient`'s `camelCaseFlagKeys`), so keys are camelCased here to
 * match — the file can use the real LD dashboard key in any casing and behave
 * identically to production.
 */
const devFlagOverrides = (): Record<string, unknown> => {
  if (!import.meta.dev) return {};

  const files: Record<string, { flagValues?: Record<string, unknown> }> = import.meta.glob(
    '../../config/flags/feature_flags.json',
    { eager: true, import: 'default' },
  );
  const flagValues = Object.values(files)[0]?.flagValues ?? {};

  return Object.fromEntries(Object.entries(flagValues).map(([key, value]) => [camelCase(key), value]));
};

export default defineNuxtPlugin(async (nuxtApp) => {
  const ldClientKey = nuxtApp.$config.public.ldClientKey;
  const $auth = useAuth();
  const userId = $auth.userProfile?.value?.sub;
  const clientId = $auth.userProfile?.value?.client || $auth.userProfile?.value?.accessible_clients?.[0]?.client?.id;

  if (!ldClientKey || !$auth.isLoggedIn.value || !userId || !clientId) {
    const client = import.meta.dev ? createMockFlagClient(devFlagOverrides()) : createNoopFlagClient();
    provideFlags(nuxtApp, client);
    return;
  }

  let client;
  try {
    client = await createFlagClient({
      clientKey: ldClientKey,
      context: { userId, clientId },
      applicationId: 'auth-service-frontend',
    });
  } catch {
    client = createNoopFlagClient();
  }
  provideFlags(nuxtApp, client);
});
